import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { mondayOf, weekDays } from "@/lib/dates";

export const usePlanner = () => {
  const [monday, setMonday] = useState(() => mondayOf(new Date()));
  const [employees, setEmployees] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [weekTasks, setWeekTasks] = useState([]);
  const [unassigned, setUnassigned] = useState([]);
  const [openTasks, setOpenTasks] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [recurring, setRecurring] = useState([]);
  const [loading, setLoading] = useState(true);
  const days = useMemo(() => weekDays(monday), [monday]);

  const refresh = useCallback(async () => {
    try {
      // Automatic recurring tasks fill this week the first time it is opened
      await api.syncRecurring({ monday: days[0].iso }).catch(() => {});
      const [emps, week, un, open, vehs, abs, rec] = await Promise.all([
        api.getEmployees(),
        api.getTasks({ start: days[0].iso, end: days[5].iso }),
        api.getTasks({ unassigned: true }),
        api.getTasks({ open_only: true }),
        api.getVehicles(),
        api.getAbsences({ start: days[0].iso, end: days[5].iso }),
        api.getTasks({ recurring: true }),
      ]);
      setRecurring(rec);
      setEmployees(emps);
      setAbsences(abs);
      setVehicles(vehs);
      setWeekTasks(week);
      setUnassigned(un);
      setOpenTasks(open);
      return true;
    } catch (e) {
      toast.error("Impossibile caricare i dati");
      return false;
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const run = useCallback(
    async (fn, message) => {
      try {
        await fn();
        if (message) toast.success(message);
        await refresh();
        return true;
      } catch (e) {
        toast.error(apiError(e));
        return false;
      }
    },
    [refresh]
  );

  return { monday, setMonday, days, employees, vehicles, weekTasks, unassigned, openTasks, absences, recurring, loading, run, refresh };
};
